import React, { useState, createElement, Component } from 'react';
import { Building2, UserCircle, Briefcase, CheckCircle2, ArrowRight } from 'lucide-react';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { MagneticButton } from '../components/animated/MagneticButton';
export function RecruitmentPage() {
  const [orgForm, setOrgForm] = useState({
    companyName: '',
    contactName: '',
    roleTitle: '',
    requirements: ''
  });
  const [candidateForm, setCandidateForm] = useState({
    fullName: '',
    roleOfInterest: '',
    linkedinUrl: '',
    cvLink: '',
    message: ''
  });
  const openMailto = (mailtoUrl: string) => {
    // Use a programmatic anchor click so the OS email handler is triggered
    // even inside sandboxed iframes (where window.location.href is blocked).
    const link = document.createElement('a');
    link.href = mailtoUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleOrgSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Recruitment Request: ${orgForm.roleTitle} at ${orgForm.companyName}`);
    const body = encodeURIComponent(`Company Name: ${orgForm.companyName}\n` + `Contact Name: ${orgForm.contactName}\n` + `Role Title: ${orgForm.roleTitle}\n\n` + `Requirements/Details:\n${orgForm.requirements}`);
    openMailto(`mailto:precious.ogbomo@myskillzhq.com?subject=${subject}&body=${body}`);
  };
  const handleCandidateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Candidate Application: ${candidateForm.fullName} for ${candidateForm.roleOfInterest}`);
    const body = encodeURIComponent(`Full Name: ${candidateForm.fullName}\n` + `Role of Interest: ${candidateForm.roleOfInterest}\n` + `LinkedIn Profile: ${candidateForm.linkedinUrl}\n` + `CV Link (Google Drive/Dropbox etc): ${candidateForm.cvLink}\n\n` + `Additional Message:\n${candidateForm.message}`);
    openMailto(`mailto:precious.ogbomo@myskillzhq.com?subject=${subject}&body=${body}`);
  };
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1407">
      <main className="flex-grow pt-20" data-id="element-1408">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-1409">
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1410">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1411">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-medium text-sm mb-6" data-id="element-1412">
                <Briefcase className="w-4 h-4" data-id="element-1413" />
                Recruitment & Headhunting
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-gray-900 mb-6" data-id="element-1414">
                Connecting Top Talent with{' '}
                <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent" data-id="element-1415">
                  Great Organizations
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8" data-id="element-1416">
                We don't just fill roles; we find the right cultural and
                technical fit. Our rigorous vetting process includes
                comprehensive background checks to ensure trust and performance.
              </p>
            </div>
          </div>
        </section>

        {/* Forms Section */}
        <section className="py-20 bg-white" data-id="element-1417">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1418">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl mx-auto" data-id="element-1419">
              {/* Organization Form */}
              <AnimatedSection data-id="element-1420">
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 relative overflow-hidden h-full" data-id="element-1421">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -z-10" data-id="element-1422" />

                  <div className="flex items-center gap-4 mb-8" data-id="element-1423">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600" data-id="element-1424">
                      <Building2 className="w-6 h-6" data-id="element-1425" />
                    </div>
                    <div data-id="element-1426">
                      <h2 className="text-2xl font-bold text-gray-900" data-id="element-1427">
                        For Organizations
                      </h2>
                      <p className="text-gray-600" data-id="element-1428">
                        Hire vetted, high-performing talent
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleOrgSubmit} className="space-y-5" data-id="element-1429">
                    <div data-id="element-1430">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1431">
                        Company Name
                      </label>
                      <input type="text" required value={orgForm.companyName} onChange={e => setOrgForm({
                      ...orgForm,
                      companyName: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all" placeholder="e.g. Acme Corp" data-id="element-1432" />
                    </div>
                    <div data-id="element-1433">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1434">
                        Contact Name
                      </label>
                      <input type="text" required value={orgForm.contactName} onChange={e => setOrgForm({
                      ...orgForm,
                      contactName: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all" placeholder="e.g. Jane Doe" data-id="element-1435" />
                    </div>
                    <div data-id="element-1436">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1437">
                        Role Title
                      </label>
                      <input type="text" required value={orgForm.roleTitle} onChange={e => setOrgForm({
                      ...orgForm,
                      roleTitle: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all" placeholder="e.g. Senior Executive Assistant" data-id="element-1438" />
                    </div>
                    <div data-id="element-1439">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1440">
                        Requirements & Details
                      </label>
                      <textarea required rows={4} value={orgForm.requirements} onChange={e => setOrgForm({
                      ...orgForm,
                      requirements: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all resize-none" placeholder="Tell us about the role, required experience, and ideal candidate profile..." data-id="element-1441" />
                    </div>
                    <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-1442">
                      Submit Request <ArrowRight className="w-4 h-4" data-id="element-1443" />
                    </button>
                    <p className="text-xs text-center text-gray-500 mt-4" data-id="element-1444">
                      This will open your default email client to send the
                      request.
                    </p>
                  </form>
                </div>
              </AnimatedSection>

              {/* Candidate Form */}
              <AnimatedSection data-id="element-1445">
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 relative overflow-hidden h-full" data-id="element-1446">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-bl-full -z-10" data-id="element-1447" />

                  <div className="flex items-center gap-4 mb-8" data-id="element-1448">
                    <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center text-green-600" data-id="element-1449">
                      <UserCircle className="w-6 h-6" data-id="element-1450" />
                    </div>
                    <div data-id="element-1451">
                      <h2 className="text-2xl font-bold text-gray-900" data-id="element-1452">
                        For Candidates
                      </h2>
                      <p className="text-gray-600" data-id="element-1453">
                        Join our vetted talent pool
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleCandidateSubmit} className="space-y-5" data-id="element-1454">
                    <div data-id="element-1455">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1456">
                        Full Name
                      </label>
                      <input type="text" required value={candidateForm.fullName} onChange={e => setCandidateForm({
                      ...candidateForm,
                      fullName: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none transition-all" placeholder="e.g. John Smith" data-id="element-1457" />
                    </div>
                    <div data-id="element-1458">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1459">
                        Role of Interest
                      </label>
                      <input type="text" required value={candidateForm.roleOfInterest} onChange={e => setCandidateForm({
                      ...candidateForm,
                      roleOfInterest: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none transition-all" placeholder="e.g. Virtual Sales Professional" data-id="element-1460" />
                    </div>
                    <div data-id="element-1461">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1462">
                        LinkedIn Profile URL
                      </label>
                      <input type="url" required value={candidateForm.linkedinUrl} onChange={e => setCandidateForm({
                      ...candidateForm,
                      linkedinUrl: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none transition-all" placeholder="https://linkedin.com/in/yourprofile" data-id="element-1463" />
                    </div>
                    <div data-id="element-1464">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1465">
                        Link to CV (Google Drive, Dropbox, etc.)
                      </label>
                      <input type="url" required value={candidateForm.cvLink} onChange={e => setCandidateForm({
                      ...candidateForm,
                      cvLink: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none transition-all" placeholder="Make sure the link is publicly accessible" data-id="element-1466" />
                    </div>
                    <div data-id="element-1467">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1468">
                        Brief Message (Optional)
                      </label>
                      <textarea rows={2} value={candidateForm.message} onChange={e => setCandidateForm({
                      ...candidateForm,
                      message: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none transition-all resize-none" placeholder="Any additional context..." data-id="element-1469" />
                    </div>
                    <button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-1470">
                      Submit Application <ArrowRight className="w-4 h-4" data-id="element-1471" />
                    </button>
                    <p className="text-xs text-center text-gray-500 mt-4" data-id="element-1472">
                      This will open your default email client to send the
                      application.
                    </p>
                  </form>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16 bg-gray-50" data-id="element-1473">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1474">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1475">
              <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1476">
                Our Vetting Process
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1477">
                <div className="flex flex-col items-center" data-id="element-1478">
                  <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-4 text-blue-600" data-id="element-1479">
                    <CheckCircle2 className="w-6 h-6" data-id="element-1480" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-1481">
                    Skills Assessment
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1482">
                    Rigorous evaluation of technical and soft skills.
                  </p>
                </div>
                <div className="flex flex-col items-center" data-id="element-1483">
                  <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-4 text-blue-600" data-id="element-1484">
                    <CheckCircle2 className="w-6 h-6" data-id="element-1485" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-1486">
                    Background Checks
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1487">
                    Comprehensive verification of employment and credentials.
                  </p>
                </div>
                <div className="flex flex-col items-center" data-id="element-1488">
                  <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-4 text-blue-600" data-id="element-1489">
                    <CheckCircle2 className="w-6 h-6" data-id="element-1490" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-1491">
                    Cultural Fit
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1492">
                    Ensuring alignment with your organization's values.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>;
}